import { parseISO } from "date-fns";

import { UserSelectionFragment } from "@src/fragments.generated";
import { getEveryoneAtOrgOrBrandLabel } from "@src/features/contentLibrary/assignmentDialogs/utils";
import { MessageBlastsInput } from "@src/types.generated";
import { NewMessagingPostFragment } from "./SentPosts.generated";
import { PostsTab } from "./postsTabs";

export const POSTS_PAGINATION_LIMIT = 15;
export const POSTS_POLL_INTERVAL_MS = 20_000;

const PREVIEW_LINES = 4;

export const findPreviewPrefix = (
  element: HTMLElement,
  text: string,
): string | null => {
  if (!text) return null;
  const probe = document.createElement("div");
  probe.className = element.className;
  probe.style.position = "absolute";
  probe.style.visibility = "hidden";
  probe.style.width = `${element.clientWidth}px`;
  element.insertAdjacentElement("afterend", probe);
  try {
    const maxHeight =
      parseFloat(getComputedStyle(probe).lineHeight) * PREVIEW_LINES + 1;
    const fits = (candidate: string, truncated: boolean) => {
      probe.textContent = truncated ? `${candidate}… ` : candidate;
      if (truncated) {
        const suffix = document.createElement("span");
        suffix.className = "font-semibold";
        suffix.textContent = "Show more";
        probe.appendChild(suffix);
      }
      return probe.clientHeight <= maxHeight;
    };
    if (fits(text, false)) return null;
    const chars = Array.from(text);
    let low = 0;
    let high = chars.length;
    while (low < high) {
      const mid = Math.ceil((low + high) / 2);
      if (fits(chars.slice(0, mid).join(""), true)) {
        low = mid;
      } else {
        high = mid - 1;
      }
    }
    return chars
      .slice(0, low)
      .join("")
      .replace(/[\s,.;:!?…]+$/, "");
  } finally {
    probe.remove();
  }
};

type UserSelectionSetLike = {
  userSelections: UserSelectionFragment[];
} | null;

export type UserSelectionSetSender = {
  org?: { name: string } | null;
  brands: { name: string }[];
};

export const getUserSelectionSetString = (
  userSelectionSet: UserSelectionSetLike | undefined,
  recipientCount: number,
  sender: UserSelectionSetSender,
): string => {
  const selections = userSelectionSet?.userSelections || [];
  if (selections.length === 0) {
    return `${recipientCount} ${recipientCount === 1 ? "recipient" : "recipients"}`;
  }

  const who: string[] = [];
  const where: string[] = [];

  selections.forEach((selection) => {
    if (selection.everyoneAtOrgOrBrand) {
      pushUnique(
        who,
        getEveryoneAtOrgOrBrandLabel(
          sender.org?.name ?? "the organization",
          sender.brands,
        ),
      );
    } else if (
      selection.allTrainees &&
      selection.allManagers &&
      selection.allAdmins
    ) {
      pushUnique(who, "Everyone");
    } else {
      if (selection.allTrainees) pushUnique(who, "All Trainees");
      if (selection.allManagers) pushUnique(who, "Managers");
      if (selection.allAdmins) pushUnique(who, "Admins");
    }
    selection.roleMemberships.forEach((m) => pushUnique(who, m.role.name));
    selection.roleGroupMemberships.forEach((m) =>
      pushUnique(who, m.roleGroup.name),
    );
    selection.userMemberships.forEach((m) => {
      if (m.user.name) pushUnique(who, m.user.name);
    });
    if (!selection.allLocations) {
      selection.locationMemberships.forEach((m) =>
        pushUnique(where, m.location.name),
      );
      selection.locationGroupMemberships.forEach((m) =>
        pushUnique(where, m.locationGroup.name),
      );
    }
  });

  if (who.length === 0 && where.length === 0) {
    return `${recipientCount} ${recipientCount === 1 ? "recipient" : "recipients"}`;
  }

  const whoLabel = who.length > 0 ? who.join(", ") : "Everyone";
  return where.length > 0 ? `${whoLabel} at ${where.join(", ")}` : whoLabel;
};

const pushUnique = (list: string[], value: string): void => {
  if (!list.includes(value)) {
    list.push(value);
  }
};

const getPostTimestamp = (blast: NewMessagingPostFragment): number => {
  const isoDate = blast.sentAt || blast.sendAt || blast.createdAt;
  return parseISO(isoDate).getTime();
};

export const getPostsInput = (tab: PostsTab): MessageBlastsInput => {
  const excludeHiddenFromNewsfeed = { value: true };
  if (tab === "drafts")
    return { draft: { value: true }, excludeHiddenFromNewsfeed };
  if (tab === "scheduled")
    return { scheduled: { value: true }, excludeHiddenFromNewsfeed };
  if (tab === "my-posts")
    return {
      draft: { value: false },
      scheduled: { value: false },
      sentByViewer: { value: true },
      excludeHiddenFromNewsfeed,
    };
  return {
    draft: { value: false },
    scheduled: { value: false },
    excludeHiddenFromNewsfeed,
  };
};

export const sortPosts = (
  blasts: NewMessagingPostFragment[],
  tab: PostsTab,
): NewMessagingPostFragment[] => {
  if (tab === "drafts") {
    return blasts;
  }
  const sorted = [...blasts];
  if (tab === "scheduled") {
    sorted.sort((a, b) => getPostTimestamp(a) - getPostTimestamp(b));
  } else {
    sorted.sort((a, b) => getPostTimestamp(b) - getPostTimestamp(a));
  }
  return sorted;
};
